import { useMemo } from "react";

import { lamportsToUiAmount, bsMath } from "@bridgesplit/utils";
import { SwapInstructionsResponse, WindType } from "@loopscale/pandora-types";
import {
    getZcCollateral,
    getZcLedger,
    calculateHealthRatio,
    useStrategySummaries,
    findStrategyLtvInfo,
    strategyIsTargetLoanCompatible,
    getFirstStrategy,
    getZcLoanLiquidationThreshold,
    getStrategyCollateralApyForDuration,
    getLoanStrategyDuration,
    AbfLoanExpanded
} from "@bridgesplit/abf-react";

import {
    NewDebtAndCollateralAmounts,
    ExecutionDetailsInputs,
    ExecutionDetailsInputsParams,
    UseCurrentStrategyLeverageCapacityResult
} from "./types";

// audit right values
export function calculateNewDebtAndCollateralAmountsFromQuote({
    loanExpanded,
    isAddingLeverage,
    quote,
    liquidationThreshold
}: {
    loanExpanded: AbfLoanExpanded | undefined;
    isAddingLeverage: boolean;
    quote: SwapInstructionsResponse | undefined;
    liquidationThreshold: number | undefined;
}): NewDebtAndCollateralAmounts {
    const collateral = getZcCollateral(loanExpanded);
    const currentDebt = getZcLedger(loanExpanded)?.ledgerDebt.total ?? 0;
    const collateralAmount = collateral?.amount ?? 0;

    if (isAddingLeverage) {
        const debtToBorrow = lamportsToUiAmount(
            quote?.quote.inputAmount ?? 0,
            loanExpanded?.principalMetadata?.decimals ?? 0
        );
        const collateralToReceive = lamportsToUiAmount(
            quote?.quote.minOutputAmount ?? 0,
            collateral?.metadata?.decimals ?? 0
        );

        const newDebtAmount = bsMath.add(currentDebt, debtToBorrow);
        const newCollateralAmount = bsMath.add(collateralAmount, collateralToReceive);

        const newDebtUsd = bsMath.mul(newDebtAmount, loanExpanded?.principalUsdPrice ?? 0);

        const newCollateralUsd = bsMath.mul(newCollateralAmount, collateral?.usdPrice ?? 0);

        const newHealth = calculateHealthRatio(newDebtUsd, newCollateralUsd, liquidationThreshold);

        return {
            newDebtAmount,
            newDebtUsd,
            newCollateralUsd,
            newCollateralAmount,
            newHealth
        };
    } else {
        // Reducing leverage: sell collateral, repay debt
        const collateralSold = lamportsToUiAmount(quote?.quote.inputAmount ?? 0, collateral?.metadata?.decimals ?? 0);
        const debtRepaid = lamportsToUiAmount(
            quote?.quote.minOutputAmount ?? 0,
            loanExpanded?.principalMetadata?.decimals ?? 0
        );

        const newDebtAmount = Math.max(bsMath.sub(currentDebt, debtRepaid) ?? 0, 0);

        const newCollateralAmount = bsMath.sub(collateralAmount, collateralSold);

        const newDebtUsd = bsMath.mul(newDebtAmount, loanExpanded?.principalUsdPrice ?? 0);
        const newCollateralUsd = bsMath.mul(newCollateralAmount, collateral?.usdPrice ?? 0);

        const newHealth = calculateHealthRatio(newDebtUsd, newCollateralUsd, liquidationThreshold);

        return {
            newDebtAmount,
            newDebtUsd,
            newCollateralUsd,
            newCollateralAmount,
            newHealth
        };
    }
}

export function getExecutionDetailsInputs({
    increaseLeverageQuote,
    repayWithCollateralQuote,
    repayWithCollateralAmount,
    collateral,
    increasePrincipalAmount,
    isIncreaseLeverage,
    principalUsdPrice,
    collateralUsdPrice,
    principalMetadata
}: ExecutionDetailsInputsParams): ExecutionDetailsInputs {
    if (isIncreaseLeverage) {
        const increasePrincipalAmountUsd = bsMath.mul(increasePrincipalAmount, principalUsdPrice);
        const receiveCollateralAmount = lamportsToUiAmount(
            increaseLeverageQuote?.quote.minOutputAmount ?? 0,
            collateral?.metadata?.decimals
        );
        const receiveCollateralAmountUsd = bsMath.mul(receiveCollateralAmount, collateralUsdPrice);

        return {
            externalQuote: increaseLeverageQuote,
            receiveUsdAmount: receiveCollateralAmountUsd,
            fairMarketValue: increasePrincipalAmountUsd,
            displayTokenType: WindType.Collateral
        };
    } else {
        const repayWithCollateralAmountUsd = bsMath.mul(repayWithCollateralAmount, collateralUsdPrice);
        const receivePrincipalToRepayAmount = lamportsToUiAmount(
            repayWithCollateralQuote?.quote.minOutputAmount ?? 0,
            principalMetadata?.decimals
        );
        const receivePrincipalToRepayAmountUsd = bsMath.mul(receivePrincipalToRepayAmount, principalUsdPrice);

        return {
            externalQuote: repayWithCollateralQuote,
            receiveUsdAmount: receivePrincipalToRepayAmountUsd,
            fairMarketValue: repayWithCollateralAmountUsd,
            displayTokenType: WindType.Principal
        };
    }
}

export function useCurrentStrategyLeverageCapacity(
    loanExpanded: AbfLoanExpanded | undefined,
    startingLeverage: number | undefined
): UseCurrentStrategyLeverageCapacityResult {
    const ledger = getZcLedger(loanExpanded);
    const collateralMint = getZcCollateral(loanExpanded)?.metadata.mint;

    // Summary endpoint with collateralTermsAssetFilter scoped to the loan's
    // collateral — both `strategyIsTargetLoanCompatible` and
    // `findStrategyLtvInfo` below only look up that one mint's terms.
    const { data: strategies, isLoading: strategiesLoading } = useStrategySummaries(
        {
            addresses: ledger?.strategy ? [ledger?.strategy] : [],
            collateralTermsAssetFilter: collateralMint ? [collateralMint] : []
        },
        !ledger?.strategy || !collateralMint
    );

    const isLoading = !loanExpanded || strategiesLoading || startingLeverage === undefined;

    const data = useMemo(() => {
        if (isLoading || !ledger?.strategy) {
            return undefined;
        }

        const strategy = getFirstStrategy(strategies);
        const collateralMint = getZcCollateral(loanExpanded)?.metadata.mint;
        if (!strategy || !collateralMint) {
            return undefined;
        }
        const isTargetLoopCompatible = strategyIsTargetLoanCompatible(strategy, loanExpanded, collateralMint);

        if (!isTargetLoopCompatible) {
            return undefined;
        }
        const strategyTotalBalance = strategy.totalBalance ?? 0;
        const currentLedgerDuration = getLoanStrategyDuration(loanExpanded);
        const increaseLeverageApy = getStrategyCollateralApyForDuration({
            strategy,
            collateralMint,
            currentLedgerDuration
        });

        const strategyCurrentLtvInfo = findStrategyLtvInfo(strategy, collateralMint);
        const increaseLeverageLiquidationThreshold = strategyCurrentLtvInfo?.liquidationThreshold;
        const strategyCurrentLtv = strategyCurrentLtvInfo?.ltv;

        // Calculate max allowed leverage
        const maxAllowedLeverage = strategyCurrentLtv ? 1 / (1 - strategyCurrentLtv) : 0;

        // If already at or above max leverage, return current leverage as max
        if (startingLeverage >= maxAllowedLeverage) {
            return {
                maxAvailableLeverage: startingLeverage,
                increaseLeverageApy,
                increaseLeverageLiquidationThreshold,
                strategyCurrentLtv,
                strategy
            };
        }

        // Calculate required borrow to reach max leverage
        const requiredBorrow = convertLeverageDeltaToBorrowInput(maxAllowedLeverage, loanExpanded);
        const actualBorrow = Math.min(requiredBorrow, strategyTotalBalance);

        // Calculate achieved leverage based on available balance
        const leverageDifference = maxAllowedLeverage - startingLeverage;
        const achievedLeverage =
            actualBorrow > 0
                ? startingLeverage + (actualBorrow / requiredBorrow) * leverageDifference
                : startingLeverage;

        return {
            maxAvailableLeverage: achievedLeverage,
            increaseLeverageApy,
            increaseLeverageLiquidationThreshold,
            strategyCurrentLtv,
            strategy
        };
    }, [isLoading, strategies, ledger?.strategy, loanExpanded, startingLeverage]);

    return { data, isLoading };
}

function convertLeverageDeltaToBorrowInput(
    targetLeverage: number | undefined,
    loanExpanded: AbfLoanExpanded | undefined
) {
    if (!targetLeverage) return 0;

    const ledger = getZcLedger(loanExpanded);
    const firstCollateral = getZcCollateral(loanExpanded);

    const principalPrice = loanExpanded?.principalUsdPrice ?? 0;
    const collateralPrice = firstCollateral?.usdPrice ?? 0;
    const collateralAmount = firstCollateral?.amount ?? 0;
    const debtAmount = ledger?.ledgerDebt.total ?? 0;

    // Current position values
    const collateralValueUsd = collateralAmount * collateralPrice;
    const debtValueUsd = debtAmount * principalPrice;

    if (!collateralValueUsd || !principalPrice) return 0;

    const targetLtv = convertLeverageToExpectedLtv(targetLeverage);

    if (targetLtv <= 0 || targetLtv >= 1) return 0;

    // Calculate new borrow value using the formula:
    // new borrowValue = (LTV * CollateralValue - debtValue) / (1 - LTV)
    const newBorrowValue = (targetLtv * collateralValueUsd - debtValueUsd) / (1 - targetLtv);

    // Convert borrow value to borrow amount in token terms
    const newBorrowAmount = newBorrowValue / principalPrice;

    return Math.max(newBorrowAmount, 0);
}

export function useCalculatePrincipalLeverageAdjustment(
    targetLeverage: number | undefined,
    startingLeverage: number | undefined,
    loanExpanded: AbfLoanExpanded | undefined,
    principalUsdOraclePrice: number | undefined,
    collateralUsdOraclePrice: number | undefined
): { principalBorrowAmount: number; principalRepayAmount: number; collateralAmountToRepay?: number } | undefined {
    return useMemo(() => {
        if (targetLeverage === undefined || !loanExpanded || startingLeverage === undefined) {
            return undefined;
        }

        // No change needed if target equals current
        if (targetLeverage === startingLeverage) {
            return { principalBorrowAmount: 0, principalRepayAmount: 0 };
        }

        if (targetLeverage > startingLeverage) {
            // Increasing leverage - need to borrow
            const principalBorrowAmount = convertLeverageDeltaToBorrowInput(targetLeverage, loanExpanded);
            return { principalBorrowAmount, principalRepayAmount: 0, collateralAmountToRepay: 0 };
        } else {
            // Decreasing leverage - need to repay
            const principalRepayAmount = convertLeverageDeltaToPrincipalRepayInput(targetLeverage, loanExpanded);
            const principalAmountToRepayInUsd = bsMath.mul(principalRepayAmount, principalUsdOraclePrice);
            const collateralAmountToRepay = bsMath.div(principalAmountToRepayInUsd, collateralUsdOraclePrice ?? 0);

            return { principalBorrowAmount: 0, principalRepayAmount, collateralAmountToRepay };
        }
    }, [targetLeverage, startingLeverage, loanExpanded]);
}

function convertLeverageDeltaToPrincipalRepayInput(
    targetLeverage: number | undefined,
    loanExpanded: AbfLoanExpanded | undefined
) {
    if (!targetLeverage) return 0;

    const ledger = getZcLedger(loanExpanded);
    const firstCollateral = getZcCollateral(loanExpanded);

    const principalPrice = loanExpanded?.principalUsdPrice ?? 0;
    const collateralPrice = firstCollateral?.usdPrice ?? 0;
    const collateralAmount = firstCollateral?.amount ?? 0;
    const debtAmount = ledger?.ledgerDebt.total ?? 0;

    // Current position values using oracle prices
    const collateralValue = collateralAmount * collateralPrice;
    const debtValue = debtAmount * principalPrice;

    if (!collateralValue || !principalPrice) return 0;

    // Convert target leverage to LTV with buffer (for deleveraging, add buffer to be safer)
    const targetLtv = convertLeverageToExpectedLtv(targetLeverage);

    // Special case: 1.0x leverage means 0% LTV, repay all debt
    if (targetLeverage <= 1.0) {
        return debtAmount; // Repay all debt
    }

    if (targetLtv <= 0 || targetLtv >= 1) return 0;

    // Calculate repayment value using the formula:
    // repaymentValue = (DebtValue - LTV * CollateralValue) / (1 - LTV)
    const repaymentValue = (debtValue - targetLtv * collateralValue) / (1 - targetLtv);

    // Convert repayment value to repayment amount in token terms
    const repaymentAmount = repaymentValue / principalPrice;

    return Math.max(repaymentAmount, 0);
}

function convertLeverageToExpectedLtv(leverage: number): number {
    if (leverage <= 1) return 0;
    return (leverage - 1) / leverage;
}

export function useGetStrategyStats(
    loanExpanded: AbfLoanExpanded | undefined,
    isIncreaseLeverage: boolean,
    increaseLeverageLiquidationThreshold: number | undefined,
    increaseLeverageApy: number | undefined
) {
    if (isIncreaseLeverage) {
        return {
            apy: increaseLeverageApy,
            liquidationThreshold: increaseLeverageLiquidationThreshold
        };
    }

    const ledger = getZcLedger(loanExpanded);
    const liquidationThreshold = getZcLoanLiquidationThreshold(loanExpanded);

    return {
        apy: ledger?.apy,
        liquidationThreshold
    };
}
